import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { StorageKey, useCompany, useStore } from 'zens-store'
import {
  Button,
  CalendarValue,
  Color,
  Spacing,
  Text,
  Select,
  displayTitlesForCalendarValue,
  OutsidePressHandler,
  Font,
  Icon,
  datePresetForValue,
  stringifyCalendarValue,
  calendarValueToInsightsFromTo,
  Weekday,
} from 'zens-storybook'
import { Href, useNumberParams, useParams, usePeriodParam, useWeekdaysParam, useWorkingHoursParam } from 'zens-router'
import { FloorSelect } from 'zens-fragment'

import { BuildingSelect } from '../building'
import { MenuMore, MenuMoreItem } from '../menu'

import InsightsCalendar from './calendar'

const styles = StyleSheet.create({
  container: {},
  content: {
    zIndex: 100,
    alignItems: 'center',
    flexDirection: 'row',
  },
  building: {
    marginRight: Spacing.S,
  },
  spacer: {
    flex: 1,
  },
  calendar: {
    flexDirection: 'row',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  date: {
    color: Color.metalGrey,
    marginLeft: Spacing.XS,
  },
  more: {
    backgroundColor: Color.simplyWhite,
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  moreTitle: {
    fontFamily: Font.medium,
  },
  skeletonInput: {
    width: 200,
  },
  skeletonMore: {
    width: 40,
    alignItems: 'center',
  },
})

export enum Item {
  Building = 1,
  Floor = 2,
  Period = 4,
  All = 7,
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorExtraItems?: { value: number; title: string }[]
  items?: Item
  downloadItems?: MenuMoreItem[]
  onDownload?: (key: string) => void
  href?: (buildingId: number, floorId?: number) => Href
}

const InsightsHead = (props: Props) => {
  const { t } = useTranslation()
  const allFloors = { value: 0, title: t('All floors'), href: props.href?.(props.buildingId) }
  const {
    companyId,
    buildingId,
    floorExtraItems = [allFloors],
    style,
    items = Item.All,
    downloadItems,
    onDownload,
    href,
    ...otherProps
  } = props
  const [showCalendar, setShowCalendar] = useState(false)
  const [{ floorId = 0 }] = useNumberParams()
  const [params] = useParams()
  const [weekdays, setWeekdays] = useWeekdaysParam()
  const [workingHours, setWorkingHours] = useWorkingHoursParam()
  const [period, setPeriod] = usePeriodParam()
  const { i18n, storage, message } = useStore()
  const company = useCompany({ companyId })

  const lastToastKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const key = `${stringifyCalendarValue(period)}|${weekdays?.join(',') || ''}`
    if (weekdays?.length && !calendarValueToInsightsFromTo(period, weekdays)) {
      if (lastToastKeyRef.current !== key) {
        lastToastKeyRef.current = key
        message.add({ message: t('An impossible combination of dates and weekdays is selected'), status: 'warning' })
      }
    } else {
      lastToastKeyRef.current = null
    }
  }, [period, weekdays])

  useEffect(() => {
    if (params.weekdays === undefined) {
      setWeekdays(weekdays)
    }
    if (params.workingHours === undefined) {
      setWorkingHours(workingHours)
    }
    if (params.from === undefined) {
      setPeriod(period)
    }
  }, [params.weekdays, params.workingHours, params.from])

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar)
    if (!showCalendar && period.length === 1) {
      setPeriod([period[0], period[0]])
    }
  }

  const periodDates = () => {
    return displayTitlesForCalendarValue(period, i18n.language).join(' - ')
  }

  const periodTitle = () => {
    const presetTitle = datePresetForValue(period, t)?.title
    if (presetTitle)
      return (
        <Text>
          {presetTitle} <Text style={styles.date}>{periodDates()}</Text>
        </Text>
      )
    return periodDates()
  }

  const itemIsActive = (item: Item) => (items & item) > 0

  const updatePeriod = (value: CalendarValue) => {
    setPeriod(value)
    storage.set(StorageKey.InsightsPeriod, stringifyCalendarValue(value), { session: true })
  }

  const updateWeekdays = (value: Weekday[]) => {
    setWeekdays(value)
    storage.set(StorageKey.InsightsWeekdays, value.join(','), { session: true })
  }

  const updateWorkingHours = (value: boolean) => {
    setWorkingHours(value)
    storage.set(StorageKey.InsightsWorkingHours, value ? 'true' : 'false', { session: true })
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        {itemIsActive(Item.Building) && (
          <BuildingSelect companyId={companyId} groupLocation style={styles.building} value={buildingId} href={href} />
        )}
        {itemIsActive(Item.Floor) && (
          <FloorSelect
            companyId={companyId}
            buildingId={buildingId}
            value={floorId}
            extraItems={floorExtraItems}
            href={(floorId) => href?.(buildingId, floorId) || ''}
          />
        )}
        <View style={styles.spacer} />
        {itemIsActive(Item.Period) && (
          <Button
            left="calendar"
            title={periodTitle()}
            light
            onPress={toggleCalendar}
            testID="InsightsHead.CalendarButton"
          />
        )}
        {downloadItems && (
          <MenuMore
            items={[
              {
                key: 'title',
                title: t('Download options'),
                disabled: true,
                style: styles.more,
                textStyle: styles.moreTitle,
              },
              ...downloadItems,
            ]}
            onSelect={(key) => onDownload?.(key as string)}
            onPress={() => setShowCalendar(false)}
            testID="InsightsHead.More"
          />
        )}
      </View>
      <OutsidePressHandler onOutsidePress={() => setShowCalendar(false)} testID="OutsidePressHandler">
        {showCalendar && (
          <View style={styles.calendar} testID="InsightsHead.Calendar">
            <InsightsCalendar
              workHours={company.data?.workHours}
              period={period}
              onChangePeriod={updatePeriod}
              workingHours={workingHours}
              onChangeWorkingHours={updateWorkingHours}
              weekdays={weekdays}
              onChangeWeekdays={updateWeekdays}
            />
          </View>
        )}
      </OutsidePressHandler>
    </View>
  )
}

interface SkeletonProps extends ViewProps {
  download?: boolean
}
InsightsHead.Skeleton = (props: SkeletonProps) => {
  const { download, ...otherProps } = props
  return (
    <View {...otherProps} style={[styles.container, props.style]}>
      <View style={styles.content}>
        <Select.Skeleton style={[styles.skeletonInput, styles.building]} />
        <Select.Skeleton style={styles.skeletonInput} />
        <View style={styles.spacer} />
        <Button.Skeleton style={styles.skeletonInput} />
        {download && (
          <View style={styles.skeletonMore}>
            <Icon name="dots" color={Color.calmGrey} />
          </View>
        )}
      </View>
    </View>
  )
}

export default InsightsHead
